import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { Portal } from '../components/Portal';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { TenantCustomField } from '../types';

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: any }> };

interface WhatsAppSession {
  name: string;
  displayName?: string;
  mePushName: string | null;
  meId: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
  cor: string;
  contactCount?: number;
}

export function CreateCampaignPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: editId } = useParams<{ id: string }>();
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [quotaNextResetDate, setQuotaNextResetDate] = useState<string | null>(null);
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: number]: boolean }>({});
  const [fileInfos, setFileInfos] = useState<{ [key: number]: { name: string, size: number, type: string } }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [campaignTemplatesList, setCampaignTemplatesList] = useState<Array<{ id: string; nome: string; messageType: string; minDelay: number; maxDelay: number; atualizadoEm: string }>>([]);
  const [loadTemplateSearch, setLoadTemplateSearch] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [advancedAddOpen, setAdvancedAddOpen] = useState(false);
  const [customFields, setCustomFields] = useState<TenantCustomField[]>([]);

  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    sessionNames: [] as string[],
    sessionName: '',
    messageType: 'sequence',
    messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
    randomDelay: 30,
    minDelay: 5,
    maxDelay: 30,
    startImmediately: true,
    scheduledFor: ''
  });

  useEffect(() => {
    loadContactTags();
    loadWhatsAppSessions();
    apiService.getCustomFields().then(setCustomFields).catch(() => setCustomFields([]));
  }, []);

  // Verificar se a quota de mensagens do tenant está estourada (só salvar rascunho nesse caso)
  useEffect(() => {
    if (!user?.tenantId && user?.role !== 'SUPERADMIN') return;
    const tenantId = user?.role === 'SUPERADMIN'
      ? localStorage.getItem('superadmin_selected_tenant')
      : user?.tenantId;
    if (!tenantId) return;
    const token = localStorage.getItem('auth_token');
    fetch(`/api/tenants/${tenantId}/quota`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data?.quota) return;
        const { maxMessagesPerMonth = 0, messagesSentThisMonth = 0, nextResetDate } = data.quota;
        setQuotaExceeded(maxMessagesPerMonth > 0 && messagesSentThisMonth >= maxMessagesPerMonth);
        if (nextResetDate) {
          const d = new Date(nextResetDate);
          setQuotaNextResetDate(d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }));
        } else {
          setQuotaNextResetDate(null);
        }
      })
      .catch(() => {});
  }, [user]);

  // Carregar campanha ao editar rascunho
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(`/api/campaigns/${editId}`);
        if (!response.ok) {
          if (response.status === 404) toast.error('Campanha não encontrada');
          else toast.error('Erro ao carregar campanha');
          navigate('/campanhas');
          return;
        }
        const c = await response.json();
        if (cancelled) return;
        if (c.status !== 'DRAFT') {
          toast.error('Só é possível editar campanhas em rascunho');
          navigate('/campanhas');
          return;
        }
        const mc = c.messageContent || {};
        const sequence = Array.isArray(mc.sequence) ? mc.sequence : (c.messageType && mc ? [{ type: c.messageType, content: mc }] : []);
        const scheduledFor = c.scheduledFor ? new Date(c.scheduledFor).toISOString().slice(0, 16) : '';
        setFormData({
          nome: c.nome || '',
          targetTags: Array.isArray(c.targetTags) ? c.targetTags : [],
          sessionNames: Array.isArray(c.sessionNames) ? c.sessionNames : [],
          sessionName: '',
          messageType: 'sequence',
          messageContent: { sequence },
          randomDelay: c.randomDelay ?? 30,
          minDelay: c.minDelay ?? 5,
          maxDelay: c.maxDelay ?? 30,
          startImmediately: c.startImmediately !== false,
          scheduledFor
        });
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error('Erro ao carregar campanha');
          navigate('/campanhas');
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId]);

  // Ao abrir a página vindo de "Usar" em /campanhas/modelos, carregar o template do localStorage
  useEffect(() => {
    if (editId) return;
    const raw = localStorage.getItem('campaign_template_to_load');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      localStorage.removeItem('campaign_template_to_load');
      const rawConfig = data.config;
      const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : (typeof rawConfig === 'string' ? (() => { try { return JSON.parse(rawConfig); } catch { return {}; } })() : {});
      const messageContent = data.messageType === 'sequence' && data.messageContent && Array.isArray(data.messageContent?.sequence)
        ? data.messageContent
        : { sequence: data.messageType && data.messageContent ? [{ type: data.messageType, content: typeof data.messageContent === 'object' ? data.messageContent : {} }] : [] };
      setFormData(prev => ({
        ...prev,
        nome: typeof config.campaignName === 'string' ? config.campaignName : prev.nome,
        messageType: 'sequence',
        messageContent,
        minDelay: data.minDelay != null ? Number(data.minDelay) : prev.minDelay,
        maxDelay: data.maxDelay != null ? Number(data.maxDelay) : prev.maxDelay,
        startImmediately: config.startImmediately !== undefined ? Boolean(config.startImmediately) : prev.startImmediately,
        scheduledFor: typeof config.scheduledFor === 'string' ? config.scheduledFor : prev.scheduledFor
      }));
      toast.success('Modelo carregado. Ajuste o que quiser e crie a campanha.');
    } catch (e) {
      console.error('Erro ao carregar modelo do localStorage:', e);
      localStorage.removeItem('campaign_template_to_load');
    }
  }, []);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const loadContactTags = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadWhatsAppSessions = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const loadCampaignTemplates = async (search?: string) => {
    setLoadingTemplates(true);
    try {
      const q = search != null ? `?search=${encodeURIComponent(search)}` : '';
      const response = await authenticatedFetch(`/api/campaigns/templates${q}`);
      if (!response.ok) throw new Error('Falha ao carregar modelos');
      const data = await response.json();
      setCampaignTemplatesList(data.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar modelos de campanha');
      setCampaignTemplatesList([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const handleSelectAllTags = () => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.length === contactTags.length
        ? []
        : contactTags.map(tag => tag.id)
    }));
  };

  const handleSelectAllSessions = () => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.length === whatsappSessions.length
        ? []
        : whatsappSessions.map(session => session.name)
    }));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = [...currentSequence];
    const [draggedItem] = newSequence.splice(draggedIndex, 1);
    newSequence.splice(dropIndex, 0, draggedItem);

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getDefaultContentForType = (type: string) => {
    switch (type) {
      case 'text': return { text: '' };
      case 'document': return { url: '', fileName: '' };
      case 'openai': return { model: '', system: '', user: '' };
      case 'groq': return { model: '', system: '', user: '' };
      case 'wait': return { waitTime: 30 };
      default: return { url: '', caption: '' };
    }
  };

  const addSequenceItem = (type: string) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = [...currentSequence, { type, content: getDefaultContentForType(type) }];
    setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
  };

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    const uploadKey = variationIndex !== undefined ? `${messageIndex}-${variationIndex}` : messageIndex.toString();
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const mediaVariations = [...(seqItem.content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...seqItem.content, mediaVariations }
            };
          }
          return seqItem;
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: { ...seqItem.content, url: data.fileUrl }
          } : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const handleSaveAsTemplate = async () => {
    const name = saveTemplateName.trim();
    if (name.length < 2) {
      toast.error('Digite um nome com pelo menos 2 caracteres');
      return;
    }
    const sequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    if (sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem antes de salvar o modelo');
      return;
    }
    setSavingTemplate(true);
    try {
      const payload = {
        nome: name,
        messageType: 'sequence',
        messageContent: { sequence },
        minDelay: formData.minDelay,
        maxDelay: formData.maxDelay,
        config: {
          campaignName: formData.nome,
          startImmediately: formData.startImmediately,
          scheduledFor: formData.scheduledFor
        }
      };
      const response = await authenticatedFetch('/api/campaigns/templates', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData.error || responseData.details || 'Falha ao salvar modelo');
      }
      toast.success('Modelo salvo! Você pode reutilizá-lo em "Usar modelo".');
      setShowSaveTemplateModal(false);
      setSaveTemplateName('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao salvar modelo';
      toast.error(msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleLoadTemplate = async (templateId: string) => {
    try {
      const response = await authenticatedFetch(`/api/campaigns/templates/${templateId}`);
      if (!response.ok) throw new Error('Template não encontrado');
      const { data } = await response.json();
      const rawConfig = data.config;
      const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : (typeof rawConfig === 'string' ? (() => { try { return JSON.parse(rawConfig); } catch { return {}; } })() : {});
      const messageContent = data.messageType === 'sequence' && data.messageContent && Array.isArray(data.messageContent.sequence)
        ? data.messageContent
        : { sequence: data.messageType && data.messageContent ? [{ type: data.messageType, content: typeof data.messageContent === 'object' ? data.messageContent : {} }] : [] };
      setFormData(prev => ({
        ...prev,
        nome: typeof config.campaignName === 'string' ? config.campaignName : prev.nome,
        messageType: 'sequence',
        messageContent,
        minDelay: data.minDelay != null ? Number(data.minDelay) : prev.minDelay,
        maxDelay: data.maxDelay != null ? Number(data.maxDelay) : prev.maxDelay,
        startImmediately: config.startImmediately !== undefined ? Boolean(config.startImmediately) : prev.startImmediately,
        scheduledFor: typeof config.scheduledFor === 'string' ? config.scheduledFor : prev.scheduledFor
      }));
      setShowLoadTemplateModal(false);
      setLoadTemplateSearch('');
      toast.success('Modelo carregado com todas as configurações. Ajuste o que quiser e crie a campanha.');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar modelo');
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o modelo "${templateName}"?`)) {
      return;
    }
    try {
      const response = await authenticatedFetch(`/api/campaigns/templates/${templateId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Falha ao excluir modelo');
      }
      toast.success('Modelo excluído com sucesso');
      loadCampaignTemplates(loadTemplateSearch || undefined);
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Erro ao excluir modelo');
    }
  };

  const buildCampaignPayload = () => {
    const sequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const isSequence = sequence.length > 1;
    let finalMessageType: string;
    let finalMessageContent: any;
    if (isSequence) {
      finalMessageType = 'sequence';
      finalMessageContent = { sequence };
    } else if (sequence.length === 1) {
      finalMessageType = sequence[0].type;
      finalMessageContent = sequence[0].content;
    } else {
      finalMessageType = 'sequence';
      finalMessageContent = { sequence: [] };
    }
    let scheduledForISO = null;
    if (!formData.startImmediately && formData.scheduledFor) {
      scheduledForISO = new Date(formData.scheduledFor).toISOString();
    }
    return {
      ...formData,
      messageType: finalMessageType,
      messageContent: finalMessageContent,
      scheduledFor: scheduledForISO
    };
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!formData.nome?.trim()) {
      toast.error('Dê um nome à campanha para salvar o rascunho');
      return;
    }
    if (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem para salvar o rascunho');
      return;
    }
    try {
      const campaignData = buildCampaignPayload();
      const url = editId ? `/api/campaigns/${editId}` : '/api/campaigns/draft';
      const response = await authenticatedFetch(url, {
        method: editId ? 'PUT' : 'POST',
        body: JSON.stringify(campaignData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors?.length) {
          throw new Error(errorData.errors.map((err: any) => err.msg).join(', '));
        }
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      toast.success(editId ? 'Rascunho atualizado!' : 'Rascunho salvo com sucesso!');
      window.dispatchEvent(new CustomEvent('quota-updated'));
      if (!editId) navigate('/campanhas');
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar rascunho');
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (quotaExceeded) {
      const renewal = quotaNextResetDate ? ` A cota será renovada em ${quotaNextResetDate}.` : '';
      toast.error(`Limite de mensagens do mês atingido. Salve como rascunho e publique quando a cota for renovada.${renewal}`);
      return;
    }

    if (formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contatos');
      return;
    }

    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    if (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    try {
      const campaignData = buildCampaignPayload();
      if (editId) {
        const putRes = await authenticatedFetch(`/api/campaigns/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(campaignData)
        });
        if (!putRes.ok) {
          const err = await putRes.json();
          throw new Error(err.error || err.errors?.map((e: any) => e.msg).join(', ') || `HTTP ${putRes.status}`);
        }
        const publishRes = await authenticatedFetch(`/api/campaigns/${editId}/publish`, { method: 'POST' });
        if (!publishRes.ok) {
          const err = await publishRes.json();
          throw new Error(err.error || `HTTP ${publishRes.status}`);
        }
        const publishData = await publishRes.json();
        toast.success('Campanha publicada com sucesso!');
        window.dispatchEvent(new CustomEvent('quota-updated'));
        navigate('/campanhas', { state: { publishedCampaign: publishData.campaign } });
        return;
      } else {
        const response = await authenticatedFetch('/api/campaigns', {
          method: 'POST',
          body: JSON.stringify(campaignData)
        });
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.errors && errorData.errors.length > 0) {
            const validationErrors = errorData.errors.map((err: any) => err.msg).join(', ');
            throw new Error(validationErrors);
          } else if (errorData.error) {
            throw new Error(errorData.error);
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        }
        toast.success('Campanha criada com sucesso!');
      }
      window.dispatchEvent(new CustomEvent('quota-updated'));
      navigate('/campanhas');
    } catch (error) {
      console.error('Erro ao criar/publicar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    }
  };

  if (loadingEdit) {
    return (
      <>
        <Header title="Campanhas" subtitle="Editar rascunho" compactTopbar />
        <PageLayout>
          <PageHeader title="Editar rascunho" breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Campanhas', href: '/campanhas' }, { label: 'Editar rascunho' }]} />
          <div className="flex items-center justify-center min-h-[280px]">
            <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-emerald-500" aria-hidden />
              <span className="text-sm">Carregando campanha...</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header
        title="Campanhas"
        subtitle={editId ? 'Editar rascunho' : 'Nova campanha'}
        compactTopbar
      />
      <PageLayout>
        <PageHeader
          title={editId ? 'Editar rascunho' : 'Nova Campanha'}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Campanhas', href: '/campanhas' },
            { label: editId ? 'Editar rascunho' : 'Nova Campanha' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {editId ? 'Editar campanha (rascunho)' : 'Configurar Campanha'}
              </h2>
            </div>
            <form onSubmit={handleCreateCampaign} className="p-5 sm:p-6 space-y-6">
              {/* Topo: instruções (esquerda) + Quando Enviar (direita) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Box de instruções - coluna esquerda */}
                <div className="rounded-xl border p-4 border-emerald-500 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/15">
                  <div className="flex items-start gap-3">
                    <div className="-mt-0.5 text-emerald-500">
                      <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M3.70186 12.0001C3.70186 7.41711 7.41711 3.70186 12.0001 3.70186C16.5831 3.70186 20.2984 7.41711 20.2984 12.0001C20.2984 16.5831 16.5831 20.2984 12.0001 20.2984C7.41711 20.2984 3.70186 16.5831 3.70186 12.0001ZM12.0001 1.90186C6.423 1.90186 1.90186 6.423 1.90186 12.0001C1.90186 17.5772 6.423 22.0984 12.0001 22.0984C17.5772 22.0984 22.0984 17.5772 22.0984 12.0001C22.0984 6.423 17.5772 1.90186 12.0001 1.90186ZM15.6197 10.7395C15.9712 10.388 15.9712 9.81819 15.6197 9.46672C15.2683 9.11525 14.6984 9.11525 14.347 9.46672L11.1894 12.6243L9.6533 11.0883C9.30183 10.7368 8.73198 10.7368 8.38051 11.0883C8.02904 11.4397 8.02904 12.0096 8.38051 12.3611L10.553 14.5335C10.7217 14.7023 10.9507 14.7971 11.1894 14.7971C11.428 14.7971 11.657 14.7023 11.8257 14.5335L15.6197 10.7395Z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">Como criar sua campanha</h4>
                      <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                        <li>• <strong>Passo 1:</strong> Dê um nome e selecione as categorias de contatos</li>
                        <li>• <strong>Passo 2:</strong> Escolha uma ou mais conexões WhatsApp</li>
                        <li>• <strong>Passo 3:</strong> Configure suas mensagens (texto, imagem, vídeo, etc.)</li>
                        <li>• <strong>Passo 4:</strong> Defina o intervalo entre mensagens e agende o envio</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Quando Enviar - coluna direita (ao lado do box de instruções) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quando Enviar
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.startImmediately}
                        onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">🚀 Iniciar imediatamente</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">A campanha será executada assim que for criada</p>
                      </div>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.startImmediately}
                        onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📅 Agendar para:</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Escolha data e hora específicas</p>
                      </div>
                    </label>
                    {!formData.startImmediately && (
                      <div className="sm:col-span-2 -mt-1">
                        <input
                          type="datetime-local"
                          value={formData.scheduledFor}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required={!formData.startImmediately}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Divisória acima do Nome da Campanha */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

              {/* Nome da Campanha - linha inteira */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome da Campanha *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Ex: Promoção Black Friday 2024"
                  required
                />
              </div>

              {/* Divisória abaixo do Nome da Campanha */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* COLUNA ESQUERDA - Conexões WhatsApp + Quando Enviar */}
                <div className="space-y-6">

                  {/* Conexões WhatsApp - mesmo padrão visual de Categorias de Contatos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Conexões WhatsApp *
                      </label>
                      {whatsappSessions.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllSessions}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                        >
                          {formData.sessionNames.length === whatsappSessions.length ? 'Desmarcar todas' : 'Selecionar todas'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Selecione as conexões que farão os envios.</p>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.</p>
                      <div className="max-h-40 overflow-y-auto">
                      {whatsappSessions.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                      ) : (
                        whatsappSessions.map((session) => (
                          <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white dark:hover:bg-gray-700/50 cursor-pointer">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={formData.sessionNames.includes(session.name)}
                                onChange={() => handleSessionToggle(session.name)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{session.mePushName || session.displayName || session.name}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">({session.displayName || session.name})</span>
                            </div>
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400 rounded-full shrink-0">
                              Ativa
                            </span>
                          </label>
                        ))
                      )}
                      </div>
                    </div>
                    {formData.sessionNames.length > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                        ✅ {formData.sessionNames.length} conexão(ões) selecionada(s)
                      </p>
                    )}
                  </div>

                  {/* Intervalo de Randomização (segundos) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Intervalo de Randomização (segundos)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Mínimo (min: 5s)</label>
                        <input
                          type="number"
                          min="5"
                          max="300"
                          value={formData.minDelay}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val)) return;
                            setFormData(prev => ({
                              ...prev,
                              minDelay: val,
                              randomDelay: Math.max(val, prev.maxDelay)
                            }));
                          }}
                          onBlur={(e) => {
                            const val = Math.max(5, parseInt(e.target.value) || 5);
                            setFormData(prev => ({
                              ...prev,
                              minDelay: val
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Máximo (min: 20s)</label>
                        <input
                          type="number"
                          min="20"
                          max="300"
                          value={formData.maxDelay}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val)) return;
                            setFormData(prev => ({
                              ...prev,
                              maxDelay: val,
                              randomDelay: val
                            }));
                          }}
                          onBlur={(e) => {
                            const val = Math.max(20, parseInt(e.target.value) || 20);
                            setFormData(prev => ({
                              ...prev,
                              maxDelay: val
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      ⏱️ Intervalo aleatório entre <strong>{formData.minDelay}s</strong> e <strong>{formData.maxDelay}s</strong> entre envios para evitar bloqueios
                    </p>
                  </div>
                </div>

                {/* COLUNA DIREITA - Categorias de Contatos */}
                <div className="space-y-6">
                  {/* Categorias de Contatos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Categorias de Contatos *
                      </label>
                      {contactTags.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllTags}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                        >
                          {formData.targetTags.length === contactTags.length ? 'Desmarcar todas' : 'Selecionar todas'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Selecione quais categorias de contatos receberão a campanha</p>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-800/50">
                      {contactTags.map((tag) => (
                        <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded hover:bg-white dark:hover:bg-gray-700/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.targetTags.includes(tag.id)}
                            onChange={() => handleTagToggle(tag.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{tag.nome}</span>
                          {tag.contactCount != null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">({tag.contactCount} contato{tag.contactCount !== 1 ? 's' : ''})</span>
                          )}
                        </label>
                      ))}
                      {contactTags.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Nenhuma categoria disponível</p>
                      )}
                    </div>
                    {formData.targetTags.length > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                        ✅ {formData.targetTags.length} categoria(s) selecionada(s)
                        {' · '}
                        <strong>{contactTags
                          .filter(t => formData.targetTags.includes(t.id))
                          .reduce((sum, t) => sum + (t.contactCount ?? 0), 0)} contatos</strong> receberão a campanha
                      </p>
                    )}
                  </div>

                </div>
              </div>

              {/* Divisória acima de Mensagens da Campanha */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

              {/* Mensagens da Campanha - largura total (uma coluna) */}
              <div className="mt-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mensagens da Campanha
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Configure o conteúdo que será enviado aos contatos.</p>
                </div>

                <div>
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Os itens são enviados na ordem abaixo. Escolha o tipo de conteúdo ao adicionar:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => addSequenceItem('text')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>💬</span> Texto
                        </button>
                        <button type="button" onClick={() => addSequenceItem('image')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>🖼️</span> Imagem
                        </button>
                        <button type="button" onClick={() => addSequenceItem('video')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>🎬</span> Vídeo
                        </button>
                        <button type="button" onClick={() => addSequenceItem('audio')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>🎵</span> Áudio
                        </button>
                        <button type="button" onClick={() => addSequenceItem('document')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>📄</span> Arquivo
                        </button>
                        <button type="button" onClick={() => addSequenceItem('wait')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                          <span>⏱️</span> Espera
                        </button>
                        <div className="relative inline-flex">
                          <button type="button" onClick={() => setAdvancedAddOpen(!advancedAddOpen)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors">
                            <span>⚙️</span> Avançado <svg className={`w-4 h-4 transition-transform ${advancedAddOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </button>
                          {advancedAddOpen && (
                            <div className="absolute left-0 top-full mt-1 py-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20">
                              <button type="button" onClick={() => { addSequenceItem('openai'); setAdvancedAddOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center gap-2">
                                🤖 OpenAI
                              </button>
                              <button type="button" onClick={() => { addSequenceItem('groq'); setAdvancedAddOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg flex items-center gap-2">
                                ⚡ Groq AI
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {('sequence' in formData.messageContent) && formData.messageContent.sequence.map((item, index) => (
                          <div
                            key={index}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                                className={`border rounded-xl p-4 transition-all cursor-move ${
                              draggedIndex === index
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 opacity-50'
                                : draggedIndex !== null
                                ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <div className="flex justify-between items-center mb-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-400 cursor-move text-xl" title="Arraste para reordenar">⋮⋮</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Passo {index + 1}</span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  item.type === 'text' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' :
                                  item.type === 'image' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' :
                                  item.type === 'video' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' :
                                  item.type === 'audio' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' :
                                  item.type === 'document' ? 'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-200' :
                                  item.type === 'wait' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' :
                                  item.type === 'openai' ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' :
                                  item.type === 'groq' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }`}>
                                  {item.type === 'text' && '💬 Texto'}
                                  {item.type === 'image' && '🖼️ Imagem'}
                                  {item.type === 'video' && '🎬 Vídeo'}
                                  {item.type === 'audio' && '🎵 Áudio'}
                                  {item.type === 'document' && '📄 Arquivo'}
                                  {item.type === 'wait' && '⏱️ Espera'}
                                  {item.type === 'openai' && '🤖 OpenAI'}
                                  {item.type === 'groq' && '⚡ Groq'}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                  const newSequence = currentSequence.filter((_, i) => i !== index);
                                  setFormData(prev => ({
                                    ...prev,
                                    messageContent: { sequence: newSequence }
                                  }));
                                }}
                                className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Remover
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Alterar tipo</label>
                                <select
                                  value={item.type}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  let newContent;
                                  switch (newType) {
                                    case 'text':
                                      newContent = { text: '' };
                                      break;
                                    case 'document':
                                      newContent = { url: '', fileName: '' };
                                      break;
                                    case 'openai':
                                      newContent = { model: '', system: '', user: '' };
                                      break;
                                    case 'groq':
                                      newContent = { model: '', system: '', user: '' };
                                      break;
                                    case 'wait':
                                      newContent = { waitTime: 30 };
                                      break;
                                    default:
                                      newContent = { url: '', caption: '' };
                                      break;
                                  }

                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                  const newSequence = currentSequence.map((seqItem, i) =>
                                    i === index ? { type: newType, content: newContent } : seqItem
                                  );
                                  setFormData(prev => ({
                                    ...prev,
                                    messageContent: { sequence: newSequence }
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="text">💬 Texto</option>
                                <option value="image">🖼️ Imagem</option>
                                <option value="video">🎬 Vídeo</option>
                                <option value="audio">🎵 Áudio</option>
                                <option value="document">📄 Arquivo</option>
                                <option value="openai">🤖 OpenAI</option>
                                <option value="groq">⚡ Groq AI</option>
                                <option value="wait">⏱️ Espera</option>
                              </select>
                              </div>

                              {item.type === 'text' && (
                                <div className="space-y-4">
                                  {/* Checkbox para usar variações */}
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`useVariations-${index}`}
                                      checked={item.content.useVariations || false}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? {
                                            ...seqItem,
                                            content: {
                                              ...seqItem.content,
                                              useVariations: e.target.checked,
                                              variations: e.target.checked ? [''] : undefined,
                                              text: e.target.checked ? undefined : seqItem.content.text
                                            }
                                          } : seqItem
                                        );
                                        setFormData(prev => ({
                                          ...prev,
                                          messageContent: { sequence: newSequence }
                                        }));
                                      }}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                                    />
                                    <label htmlFor={`useVariations-${index}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      Usar variações de texto
                                    </label>
                                  </div>

                                  {item.content.useVariations ? (
                                    /* Modo variações – cards por variação, padrão da página */
                                    <div className="space-y-3">
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        O sistema escolherá uma das variações para cada contato. Clique nas variáveis abaixo para inserir no texto.
                                      </p>
                                      {(item.content.variations || ['']).map((variation: string, varIndex: number) => (
                                        <div
                                          key={varIndex}
                                          className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-2"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                              Variação {varIndex + 1}
                                            </span>
                                            {(item.content.variations || []).length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations.splice(varIndex, 1);
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                                                title="Remover variação"
                                              >
                                                Remover
                                              </button>
                                            )}
                                          </div>
                                          <textarea
                                            placeholder="Digite o texto desta variação..."
                                            value={variation}
                                            onChange={(e) => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const newSequence = currentSequence.map((seqItem, i) => {
                                                if (i === index) {
                                                  const newVariations = [...(seqItem.content.variations || [])];
                                                  newVariations[varIndex] = e.target.value;
                                                  return {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, variations: newVariations }
                                                  };
                                                }
                                                return seqItem;
                                              });
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                                            rows={3}
                                          />
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) => {
                                            if (i === index) {
                                              const newVariations = [...(seqItem.content.variations || []), ''];
                                              return {
                                                ...seqItem,
                                                content: { ...seqItem.content, variations: newVariations }
                                              };
                                            }
                                            return seqItem;
                                          });
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                                      >
                                        <span>+</span>
                                        Nova variação
                                      </button>
                                    </div>
                                  ) : (
                                    /* Modo texto único */
                                    <div>
                                      <textarea
                                        placeholder="Digite sua mensagem... Use as variáveis abaixo para personalizar."
                                        value={item.content.text || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? { ...seqItem, content: { text: e.target.value } } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        rows={3}
                                      />
                                    </div>
                                  )}

                                  {/* Variáveis – bloco único, padrão da página */}
                                  <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Clique em uma variável para inserir no texto (ou na primeira variação vazia).
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Variáveis do sistema:</span>
                                      {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            if (item.content.useVariations) {
                                              const variations = item.content.variations || [''];
                                              let targetIndex = variations.findIndex((v: string) => !v.trim());
                                              if (targetIndex === -1) targetIndex = 0;
                                              const newSequence = currentSequence.map((seqItem: any, i: number) => {
                                                if (i === index) {
                                                  const newVariations = [...variations];
                                                  newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                  return { ...seqItem, content: { ...seqItem.content, variations: newVariations } };
                                                }
                                                return seqItem;
                                              });
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            } else {
                                              const currentText = currentSequence[index]?.content?.text || '';
                                              const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                i === index ? { ...seqItem, content: { text: currentText + variable } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }
                                          }}
                                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                    {customFields.length > 0 && (
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Campos personalizados:</span>
                                        {customFields.map((f) => {
                                          const variable = `{{${f.key}}}`;
                                          return (
                                            <button
                                              key={f.id}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                if (item.content.useVariations) {
                                                  const variations = item.content.variations || [''];
                                                  let targetIndex = variations.findIndex((v: string) => !v.trim());
                                                  if (targetIndex === -1) targetIndex = 0;
                                                  const newSequence = currentSequence.map((seqItem: any, i: number) => {
                                                    if (i === index) {
                                                      const newVariations = [...variations];
                                                      newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                      return { ...seqItem, content: { ...seqItem.content, variations: newVariations } };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                } else {
                                                  const currentText = currentSequence[index]?.content?.text || '';
                                                  const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                    i === index ? { ...seqItem, content: { text: currentText + variable } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }
                                              }}
                                              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                            >
                                              {variable}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {item.type === 'openai' && (
                                <div className="space-y-4">
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Configure o modelo e os prompts para a IA gerar mensagens personalizadas com base nos dados do contato.
                                  </p>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Modelo *
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Ex: gpt-3.5-turbo"
                                      value={item.content.model || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, model: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">Modelos sugeridos:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                                        <button
                                          key={model}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { ...seqItem.content, model } } : seqItem
                                            );
                                            setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                          }}
                                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                          {model}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Prompt do Sistema *
                                    </label>
                                    <textarea
                                      placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                      value={item.content.system || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, system: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      rows={3}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Define como a IA deve se comportar e responder
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Prompt do Usuário *
                                    </label>
                                    <textarea
                                      placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}..."
                                      value={item.content.user || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, user: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      rows={4}
                                    />
                                    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-3 mt-2">
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Clique em uma variável para inserir no prompt.
                                      </p>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Variáveis do sistema:</span>
                                        {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                i === index ? { ...seqItem, content: { ...seqItem.content, user: currentUser + variable } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      {customFields.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Campos personalizados:</span>
                                          {customFields.map((f) => {
                                            const variable = `{{${f.key}}}`;
                                            return (
                                              <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const currentUser = currentSequence[index]?.content?.user || '';
                                                  const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, user: currentUser + variable } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                              >
                                                {variable}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                    </p>
                                  </div>
                                </div>
                              )}

                              {item.type === 'groq' && (
                                <div className="space-y-4">
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Configure o modelo e os prompts para a IA gerar mensagens personalizadas com base nos dados do contato (Groq com inferência rápida).
                                  </p>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Modelo *
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Ex: llama-3.1-8b-instant"
                                      value={item.content.model || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, model: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">Modelos sugeridos:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.2-11b-text-preview', 'mixtral-8x7b-32768', 'gemma2-9b-it'].map((model) => (
                                        <button
                                          key={model}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { ...seqItem.content, model } } : seqItem
                                            );
                                            setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                          }}
                                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                          {model}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Prompt do Sistema *
                                    </label>
                                    <textarea
                                      placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                      value={item.content.system || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, system: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      rows={3}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Define como a IA deve se comportar e responder
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Prompt do Usuário *
                                    </label>
                                    <textarea
                                      placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}..."
                                      value={item.content.user || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? { ...seqItem, content: { ...seqItem.content, user: e.target.value } } : seqItem
                                        );
                                        setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      rows={4}
                                    />
                                    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-3 mt-2">
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Clique em uma variável para inserir no prompt.
                                      </p>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Variáveis do sistema:</span>
                                        {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                i === index ? { ...seqItem, content: { ...seqItem.content, user: currentUser + variable } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      {customFields.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Campos personalizados:</span>
                                          {customFields.map((f) => {
                                            const variable = `{{${f.key}}}`;
                                            return (
                                              <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const currentUser = currentSequence[index]?.content?.user || '';
                                                  const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, user: currentUser + variable } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                              >
                                                {variable}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                    </p>
                                  </div>
                                </div>
                              )}

                              {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                <div className="space-y-4">
                                  {/* Mesmo padrão do texto: checkbox + label */}
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`useMediaVariations-${index}`}
                                      checked={item.content.useMediaVariations || false}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) => {
                                          if (i === index) {
                                            return {
                                              ...seqItem,
                                              content: {
                                                ...seqItem.content,
                                                useMediaVariations: e.target.checked,
                                                mediaVariations: e.target.checked ? [{ url: '', caption: '', fileName: '' }] : undefined,
                                                url: e.target.checked ? '' : seqItem.content.url
                                              }
                                            };
                                          }
                                          return seqItem;
                                        });
                                        setFormData(prev => ({
                                          ...prev,
                                          messageContent: { sequence: newSequence }
                                        }));
                                      }}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                                    />
                                    <label htmlFor={`useMediaVariations-${index}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      Usar variações de {item.type === 'image' ? 'imagem' : item.type === 'video' ? 'vídeo' : item.type === 'audio' ? 'áudio' : 'arquivo'}
                                    </label>
                                  </div>

                                  {item.content.useMediaVariations ? (
                                    /* Cards por variação lado a lado + Nova variação */
                                    <div className="space-y-3">
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        O sistema escolherá uma das variações para cada contato. Faça upload de um arquivo em cada card.
                                      </p>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                      {(item.content.mediaVariations || [{ url: '', caption: '', fileName: '' }]).map((variation: { url: string; caption?: string; fileName?: string }, varIndex: number) => {
                                        const hasFile = variation.url;
                                        return (
                                          <div
                                            key={varIndex}
                                            className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-2 min-w-0"
                                          >
                                            <div className="flex items-center justify-between gap-1">
                                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                                                Variação {varIndex + 1}
                                              </span>
                                              {(item.content.mediaVariations || []).length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                    const newVariations = (item.content.mediaVariations || []).filter((_, i) => i !== varIndex);
                                                    const newSequence = currentSequence.map((seqItem, i) =>
                                                      i === index
                                                        ? { ...seqItem, content: { ...seqItem.content, mediaVariations: newVariations.length ? newVariations : [{ url: '', caption: '', fileName: '' }] } }
                                                      : seqItem
                                                    );
                                                    setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                  }}
                                                  className="text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors shrink-0"
                                                  title="Remover variação"
                                                >
                                                  Remover
                                                </button>
                                              )}
                                            </div>

                                            <div className="aspect-square w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                                              {!hasFile ? (
                                                <>
                                                  <input
                                                    type="file"
                                                    id={`file-upload-${index}-${varIndex}`}
                                                    className="hidden"
                                                    accept={
                                                      item.type === 'image' ? 'image/*' :
                                                      item.type === 'video' ? 'video/*' :
                                                      item.type === 'audio' ? 'audio/*' :
                                                      'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                    }
                                                    onChange={(e) => {
                                                      const file = e.target.files?.[0];
                                                      if (file) handleFileUpload(file, index, varIndex);
                                                    }}
                                                    disabled={uploadingFiles[`${index}-${varIndex}`]}
                                                  />
                                                  <label
                                                    htmlFor={`file-upload-${index}-${varIndex}`}
                                                    className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors border-0 border-transparent rounded-lg ${
                                                      uploadingFiles[`${index}-${varIndex}`]
                                                        ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400'
                                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                    }`}
                                                  >
                                                    {uploadingFiles[`${index}-${varIndex}`] ? (
                                                      <div className="flex flex-col items-center gap-1">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300" />
                                                        <span className="text-xs">Enviando...</span>
                                                      </div>
                                                    ) : (
                                                      <div className="flex flex-col items-center gap-1">
                                                        <div className="text-2xl">
                                                          {item.type === 'image' && '🖼️'}
                                                          {item.type === 'video' && '🎥'}
                                                          {item.type === 'audio' && '🎵'}
                                                          {item.type === 'document' && '📄'}
                                                        </div>
                                                        <span className="text-xs">Fazer upload</span>
                                                      </div>
                                                    )}
                                                  </label>
                                                </>
                                              ) : (
                                                <div className="w-full h-full relative group">
                                                  {item.type === 'image' && (
                                                    <img src={variation.url} alt={`Variação ${varIndex + 1}`} className="w-full h-full object-cover" />
                                                  )}
                                                  {item.type === 'video' && (
                                                    <div className="w-full h-full relative">
                                                      <video src={variation.url} className="w-full h-full object-cover" muted />
                                                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                        <div className="bg-white/90 p-1 rounded-full">
                                                          <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                  {item.type === 'audio' && (
                                                    <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 flex flex-col items-center justify-center">
                                                      <div className="text-3xl mb-1">🎵</div>
                                                      <div className="text-xs text-center text-gray-700 dark:text-gray-300 px-1">Áudio</div>
                                                    </div>
                                                  )}
                                                  {item.type === 'document' && (
                                                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex flex-col items-center justify-center p-2">
                                                      <div className="text-3xl mb-1">📄</div>
                                                      <div className="text-xs text-center text-gray-700 dark:text-gray-300 truncate w-full">{variation.fileName || 'Documento'}</div>
                                                    </div>
                                                  )}
                                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                    <a href={variation.url} target="_blank" rel="noopener noreferrer" className="bg-white/90 px-2 py-1 rounded text-xs font-medium text-gray-800">
                                                      Ver
                                                    </a>
                                                  </div>
                                                </div>
                                              )}
                                            </div>

                                            {hasFile && ['image', 'video'].includes(item.type) && (
                                              <input
                                                type="text"
                                                placeholder="Legenda (opcional)"
                                                value={variation.caption || ''}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                      while (newVariations.length <= varIndex) newVariations.push({ url: '', caption: '', fileName: '' });
                                                      newVariations[varIndex] = { ...newVariations[varIndex], caption: e.target.value };
                                                      return { ...seqItem, content: { ...seqItem.content, mediaVariations: newVariations } };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                              />
                                            )}
                                            {hasFile && !['image', 'video'].includes(item.type) && (
                                              <div className="text-xs text-gray-600 dark:text-gray-400 truncate" title={variation.fileName}>{variation.fileName || 'Arquivo'}</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) => {
                                            if (i === index) {
                                              const current = seqItem.content.mediaVariations || [{ url: '', caption: '', fileName: '' }];
                                              return { ...seqItem, content: { ...seqItem.content, mediaVariations: [...current, { url: '', caption: '', fileName: '' }] } };
                                            }
                                            return seqItem;
                                          });
                                          setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                        }}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                                      >
                                        <span>+</span>
                                        Nova variação
                                      </button>
                                    </div>
                                  ) : (
                                    // Interface original de arquivo único
                                    <>
                                      {!item.content.url ? (
                                        <div className="flex items-center gap-3">
                                          <input
                                            type="file"
                                            id={`file-upload-${index}`}
                                            className="hidden"
                                            accept={
                                              item.type === 'image' ? 'image/*' :
                                              item.type === 'video' ? 'video/*' :
                                              item.type === 'audio' ? 'audio/*' :
                                              'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                            }
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) {
                                                handleFileUpload(file, index);
                                              }
                                            }}
                                            disabled={uploadingFiles[index]}
                                          />
                                          <label
                                            htmlFor={`file-upload-${index}`}
                                            className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                              uploadingFiles[index]
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                                : 'bg-white text-gray-700'
                                            }`}
                                          >
                                            {uploadingFiles[index] ? (
                                              <div className="flex flex-col items-center gap-2">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                <span>Enviando arquivo...</span>
                                              </div>
                                            ) : (
                                              <div className="flex flex-col items-center gap-2">
                                                <div className="text-2xl">📁</div>
                                                <span>Clique para fazer upload do arquivo</span>
                                                <span className="text-xs text-gray-500">
                                                  {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                                  {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                                  {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                                  {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                                </span>
                                              </div>
                                            )}
                                          </label>
                                        </div>
                                      ) : (
                                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                              {item.type === 'image' && (
                                                <img
                                                  src={item.content.url}
                                                  alt="Preview"
                                                  className="w-12 h-12 object-cover rounded"
                                                />
                                              )}
                                              {item.type !== 'image' && (
                                                <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                  {getFileIcon(fileInfos[index]?.type || item.type)}
                                                </div>
                                              )}
                                              <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">
                                                  {fileInfos[index]?.name || 'Arquivo carregado'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : ''}
                                                </div>
                                              </div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveFile(index)}
                                              className="text-red-600 hover:text-red-800 p-1"
                                              title="Remover arquivo"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                              </svg>
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {['image', 'video'].includes(item.type) && !item.content.useMediaVariations && (
                                    <div className="space-y-2">
                                      <input
                                        type="text"
                                        placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, etc."
                                        value={item.content.caption || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, caption: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="space-y-2">
                                        <div className="flex flex-wrap items-center gap-1">
                                          <span className="text-xs text-gray-500 dark:text-gray-400">Variáveis do sistema:</span>
                                          {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                            <button
                                              key={variable}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const currentCaption = currentSequence[index]?.content?.caption || '';
                                                const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                  i === index ? { ...seqItem, content: { ...seqItem.content, caption: currentCaption + variable } } : seqItem
                                                );
                                                setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                              }}
                                              className="px-2 py-1 bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200 text-xs rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                                            >
                                              {variable}
                                            </button>
                                          ))}
                                        </div>
                                        {customFields.length > 0 && (
                                          <div className="flex flex-wrap items-center gap-1">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Campos personalizados:</span>
                                            {customFields.map((f) => {
                                              const variable = `{{${f.key}}}`;
                                              return (
                                                <button
                                                  key={f.id}
                                                  type="button"
                                                  onClick={() => {
                                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                    const currentCaption = currentSequence[index]?.content?.caption || '';
                                                    const newSequence = currentSequence.map((seqItem: any, i: number) =>
                                                      i === index ? { ...seqItem, content: { ...seqItem.content, caption: currentCaption + variable } } : seqItem
                                                    );
                                                    setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                  }}
                                                  className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                                                >
                                                  {variable}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {item.type === 'document' && !item.content.useMediaVariations && (
                                    <input
                                      type="text"
                                      placeholder="Nome do arquivo (opcional)"
                                      value={item.content.fileName || ''}
                                      onChange={(e) => {
                                        const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                        const newSequence = currentSequence.map((seqItem, i) =>
                                          i === index ? {
                                            ...seqItem,
                                            content: { ...seqItem.content, fileName: e.target.value }
                                          } : seqItem
                                        );
                                        setFormData(prev => ({
                                          ...prev,
                                          messageContent: { sequence: newSequence }
                                        }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  )}
                                </div>
                              )}

                              {item.type === 'wait' && (
                                <div className="space-y-3">
                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                      <span className="text-2xl">⏱️</span>
                                      <div className="flex-1">
                                        <h4 className="text-sm font-medium text-yellow-900 mb-2">
                                          Tempo de Espera
                                        </h4>
                                        <p className="text-xs text-yellow-700 mb-3">
                                          Aguarda um tempo antes de enviar a próxima mensagem
                                        </p>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="number"
                                            min="1"
                                            max="3600"
                                            value={item.content.waitTime ?? ''}
                                            onChange={(e) => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const value = e.target.value === '' ? '' : parseInt(e.target.value);
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { waitTime: value }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="w-24 px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                                          />
                                          <span className="text-sm text-yellow-700">segundos</span>
                                        </div>
                                        <p className="text-xs text-yellow-600 mt-2">
                                          Mínimo: 1 segundo | Máximo: 3600 segundos (1 hora)
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {(!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) && (
                          <div className="text-center py-12 px-4 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
                            <div className="text-4xl mb-4">📋</div>
                            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Nenhum item na sequência</p>
                            <p className="text-sm mt-1 max-w-md mx-auto">Use os botões acima para adicionar <strong>texto</strong>, <strong>imagem</strong>, <strong>vídeo</strong>, <strong>áudio</strong>, <strong>arquivo</strong> ou uma <strong>espera</strong> entre envios. A ordem aqui é a ordem de envio.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
              </div>

              {quotaExceeded && (
                <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-2">
                  Limite de mensagens do mês atingido. Você pode salvar como rascunho e publicar quando a cota for renovada.
                  {quotaNextResetDate && (
                    <> A cota será renovada em <strong>{quotaNextResetDate}</strong>.</>
                  )}
                </p>
              )}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/campanhas')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => setShowSaveTemplateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-save text-base shrink-0" aria-hidden />
                  Salvar como modelo
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/30 transition-colors"
                >
                  <i className="fas fa-file-pen text-base shrink-0" aria-hidden />
                  Salvar rascunho
                </button>
                {!quotaExceeded && (
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  >
                    <i className={`fas ${editId ? 'fa-paper-plane' : 'fa-plus'} text-base shrink-0`} aria-hidden />
                    {editId ? 'Publicar campanha' : 'Criar Campanha'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </PageLayout>

      {/* Modal Salvar como modelo */}
      {showSaveTemplateModal && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 10000 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Salvar como modelo</h3>
              <p className="text-sm text-gray-600 mb-4">Dê um nome a este modelo para reutilizar a mesma mensagem em outra campanha ou lista depois.</p>
              <input
                type="text"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                placeholder="Ex: Promo Black Friday"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                autoFocus
              />
              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowSaveTemplateModal(false); setSaveTemplateName(''); }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  disabled={savingTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingTemplate ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save text-base shrink-0" aria-hidden />
                      Salvar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal Usar modelo */}
      {showLoadTemplateModal && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 10000 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Usar modelo</h3>
                <button
                  onClick={() => { setShowLoadTemplateModal(false); setLoadTemplateSearch(''); }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-4 border-b">
                <input
                  type="text"
                  value={loadTemplateSearch}
                  onChange={(e) => { setLoadTemplateSearch(e.target.value); loadCampaignTemplates(e.target.value || undefined); }}
                  placeholder="Buscar por nome..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                {loadingTemplates ? (
                  <p className="text-gray-500 text-center py-6">Carregando...</p>
                ) : campaignTemplatesList.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">Nenhum modelo salvo. Crie uma campanha e use "Salvar como modelo".</p>
                ) : (
                  <ul className="space-y-2">
                    {campaignTemplatesList.map((t) => (
                      <li key={t.id} className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleLoadTemplate(t.id)}
                          className="flex-1 text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{t.nome}</span>
                          <span className="block text-xs text-gray-500 mt-1">Tipo: {t.messageType} · Intervalo: {t.minDelay}s–{t.maxDelay}s</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(t.id, t.nome);
                          }}
                          className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors self-start"
                          title="Excluir modelo"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
